import React, { useCallback, useEffect, useState } from 'react';
import { authAPI } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { toast } from 'sonner';
import { Shield, ShieldCheck, ShieldAlert, Copy, Download, KeyRound, AlertTriangle } from 'lucide-react';

const CODE_INPUT_RE = /^[0-9]{0,6}$/;

export default function SecurityTab() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Enrolment state
  const [enrolment, setEnrolment] = useState(null); // { secret, qr_data_url }
  const [enrolmentCode, setEnrolmentCode] = useState('');
  const [enrolling, setEnrolling] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [codesAcknowledged, setCodesAcknowledged] = useState(false);

  // Disable state
  const [disableOpen, setDisableOpen] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [disabling, setDisabling] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authAPI.mfaStatus();
      setStatus(data);
    } catch (e) {
      toast.error('Failed to load MFA status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const startEnrolment = async () => {
    try {
      const { data } = await authAPI.mfaSetup();
      setEnrolment(data);
      setEnrolmentCode('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start MFA setup');
    }
  };

  const confirmEnrolment = async () => {
    if (enrolmentCode.length !== 6) {
      toast.error('Enter the 6-digit code from your authenticator app');
      return;
    }
    setEnrolling(true);
    try {
      const { data } = await authAPI.mfaVerifySetup(enrolmentCode);
      setBackupCodes(data.backup_codes || []);
      setCodesAcknowledged(false);
      setEnrolment(null);
      toast.success('MFA enabled on your account');
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verification failed');
    } finally {
      setEnrolling(false);
    }
  };

  const disableMfa = async () => {
    if (disableCode.length < 6) {
      toast.error('Enter your 6-digit code or a backup code');
      return;
    }
    setDisabling(true);
    try {
      await authAPI.mfaDisable(disableCode);
      toast.success('MFA disabled on your account');
      setDisableOpen(false);
      setDisableCode('');
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not disable MFA');
    } finally {
      setDisabling(false);
    }
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(enrolment?.secret || '');
      toast.success('Secret copied to clipboard');
    } catch {
      toast.error('Clipboard unavailable — type the secret manually');
    }
  };

  const copyBackupCodes = async () => {
    try {
      await navigator.clipboard.writeText((backupCodes || []).join('\n'));
      toast.success('Backup codes copied');
    } catch {
      toast.error('Clipboard unavailable');
    }
  };

  const downloadBackupCodes = () => {
    const blob = new Blob(
      [
        'Elara Workforce — MFA backup codes\n',
        'Keep these safe. Each code can be used ONCE if you lose access to your authenticator.\n\n',
        (backupCodes || []).join('\n'),
        '\n',
      ],
      { type: 'text/plain' },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'elara-mfa-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const closeBackupCodesDialog = () => {
    if (!codesAcknowledged) {
      toast.error('Please tick the acknowledgement before continuing');
      return;
    }
    setBackupCodes(null);
  };

  if (loading) {
    return (
      <div className="py-10 text-center text-zinc-400" data-testid="mfa-loading">Loading security settings…</div>
    );
  }

  const isEnabled = !!status?.enabled;

  return (
    <div className="space-y-6" data-testid="security-tab">
      <Card className="bg-[#2B2B2B] border-white/5">
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div
                className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                  isEnabled ? 'bg-emerald-500/15' : 'bg-amber-500/15'
                }`}
              >
                {isEnabled ? (
                  <ShieldCheck className="h-5 w-5 text-emerald-400" />
                ) : (
                  <ShieldAlert className="h-5 w-5 text-amber-400" />
                )}
              </div>
              <div>
                <CardTitle className="text-[#F2F2F2]">Two-factor authentication</CardTitle>
                <CardDescription className="text-zinc-400">
                  Add a time-based one-time code to your sign-in. Required for platform-admin access.
                </CardDescription>
              </div>
            </div>
            {isEnabled ? (
              <Badge className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" data-testid="mfa-status-badge">
                Enabled
              </Badge>
            ) : (
              <Badge className="bg-amber-500/15 text-amber-400 border border-amber-500/30" data-testid="mfa-status-badge">
                Not enabled
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isEnabled ? (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="p-3 rounded-md border border-zinc-800 bg-[#1A1A1A]">
                  <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">
                    Enabled on
                  </div>
                  <div className="text-[#F2F2F2] text-sm mt-1" data-testid="mfa-enabled-at">
                    {status?.enabled_at ? status.enabled_at.slice(0, 10) : '—'}
                  </div>
                </div>
                <div className="p-3 rounded-md border border-zinc-800 bg-[#1A1A1A]">
                  <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">
                    Backup codes remaining
                  </div>
                  <div className="text-[#F2F2F2] text-sm mt-1" data-testid="mfa-backup-remaining">
                    {status?.backup_codes_remaining ?? 0} / 10
                  </div>
                </div>
              </div>
              {status?.backup_codes_remaining <= 2 && (
                <Alert className="bg-amber-500/10 border-amber-500/30">
                  <AlertTriangle className="h-4 w-4 text-amber-400" />
                  <AlertTitle className="text-amber-400">Backup codes running low</AlertTitle>
                  <AlertDescription className="text-zinc-300">
                    Disable and re-enable MFA to generate a fresh set of 10 single-use backup codes.
                  </AlertDescription>
                </Alert>
              )}
              <div>
                <Button
                  variant="outline"
                  className="border-rose-500/40 text-rose-400 hover:bg-rose-500/10"
                  onClick={() => setDisableOpen(true)}
                  data-testid="mfa-disable-btn"
                >
                  Disable MFA
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <Alert className="bg-[#1A1A1A] border-white/5">
                <Shield className="h-4 w-4 text-[#1ABC9C]" />
                <AlertTitle className="text-[#F2F2F2]">Protect this account with a second step</AlertTitle>
                <AlertDescription className="text-zinc-400">
                  Use any authenticator app — Google Authenticator, 1Password, Authy — to generate a 6-digit code each
                  time you sign in. We'll also issue 10 single-use backup codes.
                </AlertDescription>
              </Alert>
              <Button
                className="bg-[#1ABC9C] hover:bg-[#17a98b] text-white"
                onClick={startEnrolment}
                data-testid="mfa-enable-btn"
              >
                <KeyRound className="h-4 w-4 mr-2" /> Enable two-factor authentication
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Enrolment dialog */}
      <Dialog open={!!enrolment} onOpenChange={(v) => !v && setEnrolment(null)}>
        <DialogContent className="bg-[#1A1A1A] border-white/5 text-[#F2F2F2] max-w-md" data-testid="mfa-enrol-dialog">
          <DialogHeader>
            <DialogTitle>Scan the QR code</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Open your authenticator app and add a new account. Then enter the 6-digit code it shows.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex justify-center">
              {enrolment?.qr_data_url && (
                <img
                  src={enrolment.qr_data_url}
                  alt="MFA QR code"
                  className="w-48 h-48 rounded-md bg-white p-2"
                  data-testid="mfa-qr-image"
                />
              )}
            </div>
            <div className="p-3 rounded-md border border-zinc-800 bg-[#2B2B2B] flex items-center justify-between gap-2">
              <code className="text-xs text-zinc-300 break-all" data-testid="mfa-secret-text">{enrolment?.secret}</code>
              <Button size="sm" variant="outline" onClick={copySecret} className="shrink-0 border-zinc-700 text-zinc-200">
                <Copy className="h-3.5 w-3.5 mr-1" /> Copy
              </Button>
            </div>
            <div>
              <label className="text-xs text-zinc-400">Verification code</label>
              <Input
                value={enrolmentCode}
                onChange={(e) => {
                  const v = e.target.value;
                  if (CODE_INPUT_RE.test(v)) setEnrolmentCode(v);
                }}
                placeholder="123456"
                inputMode="numeric"
                autoComplete="one-time-code"
                className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2] mt-1 tracking-widest text-center text-lg"
                data-testid="mfa-enrol-code-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEnrolment(null)} className="border-zinc-700 text-zinc-200">
              Cancel
            </Button>
            <Button
              onClick={confirmEnrolment}
              disabled={enrolling || enrolmentCode.length !== 6}
              className="bg-[#1ABC9C] hover:bg-[#17a98b] text-white"
              data-testid="mfa-enrol-confirm-btn"
            >
              {enrolling ? 'Verifying…' : 'Enable MFA'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backup codes dialog */}
      <Dialog open={!!backupCodes} onOpenChange={(v) => !v && closeBackupCodesDialog()}>
        <DialogContent
          className="bg-[#1A1A1A] border-white/5 text-[#F2F2F2] max-w-lg"
          onInteractOutside={(e) => e.preventDefault()}
          data-testid="mfa-backup-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-emerald-400 flex items-center gap-2">
              <ShieldCheck className="h-5 w-5" /> Two-factor authentication enabled
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Save these backup codes somewhere safe. Each one can be used ONCE if you lose access to your
              authenticator app.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2 p-4 rounded-md bg-[#2B2B2B] border border-zinc-800">
              {(backupCodes || []).map((c, i) => (
                <code key={i} className="text-xs font-mono text-zinc-200" data-testid={`backup-code-${i}`}>
                  {c}
                </code>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={copyBackupCodes} className="border-zinc-700 text-zinc-200" data-testid="mfa-copy-codes-btn">
                <Copy className="h-4 w-4 mr-1" /> Copy
              </Button>
              <Button variant="outline" onClick={downloadBackupCodes} className="border-zinc-700 text-zinc-200" data-testid="mfa-download-codes-btn">
                <Download className="h-4 w-4 mr-1" /> Download
              </Button>
            </div>
            <label className="flex items-start gap-2 text-sm text-zinc-300 cursor-pointer">
              <input
                type="checkbox"
                checked={codesAcknowledged}
                onChange={(e) => setCodesAcknowledged(e.target.checked)}
                className="mt-1 accent-[#1ABC9C]"
                data-testid="mfa-codes-ack-checkbox"
              />
              I have saved these backup codes somewhere secure.
            </label>
          </div>
          <DialogFooter>
            <Button
              onClick={closeBackupCodesDialog}
              disabled={!codesAcknowledged}
              className="bg-[#1ABC9C] hover:bg-[#17a98b] text-white"
              data-testid="mfa-codes-done-btn"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable dialog */}
      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent className="bg-[#1A1A1A] border-white/5 text-[#F2F2F2] max-w-sm" data-testid="mfa-disable-dialog">
          <DialogHeader>
            <DialogTitle>Disable two-factor authentication</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Confirm your identity with your current 6-digit code or a backup code.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={disableCode}
            onChange={(e) => setDisableCode(e.target.value)}
            placeholder="123456 or ABCD-EFGH-IJ"
            inputMode="numeric"
            autoComplete="one-time-code"
            className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2] tracking-widest text-center text-lg"
            data-testid="mfa-disable-code-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisableOpen(false)} className="border-zinc-700 text-zinc-200">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={disableMfa}
              disabled={disabling || disableCode.length < 6}
              data-testid="mfa-disable-confirm-btn"
            >
              {disabling ? 'Disabling…' : 'Disable MFA'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
